import {
  TextField,
  Select,
  InputLabel,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem as Option,
} from "@mui/material";
import { useState, useEffect, Fragment, useRef, forwardRef, useImperativeHandle, useMemo, useCallback } from "react";
// Removed direct database imports - using API instead
import { dataAPI } from "../api/dataApi";
import { IComponent, componentDef, collectionSettingsSectionMeta } from "../../definitions/componentDef";
import EditComponentField from "./EditComponentField";
import SortableFields from "./SortableFields";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { IComponentData } from "@/reducers/component";
import { ActionTypes } from "@/actions";
import { componentUpdate } from "../api/componentData";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button as ShadcnButton } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Type, Database, Hash, Building2, AlertCircle, Download, Upload, LayoutGrid } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-toastify";
import { useAuth } from "../auth/AuthContext";
import DebugFieldButton from "@/components/DebugFieldButton";
import { usePermission } from "../acl/usePermission";
import type { PermissionCheckRequest } from "../api/aclApi";
import { EMPTY_ARRAY } from "@/constants/reduxStableRefs";
import { fetchTenantList, type TenantListItem } from "../api/tenantsApi";
import { PhosphorFillIconPicker } from "./PhosphorFillIconPicker";
import { normalizeOrgSegment, parseCollectionTablePath } from "../utils/collectionPaths";
import { isOrganizationScopingDisabled } from "../utils/organizationScoping";
import { invalidateAllCachesUsedByFetchMeta } from "../api/tableData";
import { clearItemsCache } from "../api/itemsApi";
import { reloadAppConfig } from "../acl/reloadAppConfig";
import { clearRelationLabelCache } from "../component/TableHelper";

const IDENTITY_SETTING_KEYS = new Set(["name", "slug"]);
const FIELD_LIST_DEF_KEY = "fields";

const SETTING_FIELD_CARD = "mb-5 hover:shadow-md transition-shadow";
const SETTING_FIELD_CARD_CONTENT = "p-5";
const SETTING_FIELD_STACK = "space-y-4";
const SETTING_FIELD_LABEL =
  "text-sm font-medium text-gray-900 dark:text-gray-100 flex-1 min-w-0";
const SETTING_SELECT_TRIGGER =
  "h-9 w-full border-gray-200 bg-white text-sm dark:border-gray-600 dark:bg-gray-900";
const SETTING_SECTION_FIRST = "mb-4";
const SETTING_SECTION_DIVIDER =
  "mt-8 pt-6 mb-4 border-t border-gray-200 dark:border-gray-700";
const SETTING_FORM_PANEL = "space-y-3 bg-white dark:bg-gray-900 px-8 py-8";
const COLLECTION_EDITOR_TABS_LIST =
  "mb-0 inline-flex h-auto w-fit justify-start rounded-t-lg rounded-b-none border border-b-0 border-gray-200 bg-white p-1.5 shadow-sm dark:border-gray-700 dark:bg-gray-900";
const COLLECTION_EDITOR_TABS_TRIGGER =
  "rounded-md px-4 py-2 text-sm data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-blue-950/50 dark:data-[state=active]:text-blue-100";
const COLLECTION_EDITOR_TABS_CONTENT = "mt-0 focus-visible:outline-none";
const COLLECTION_EDITOR_TABS_PANEL =
  "rounded-t-none border-t-0 shadow-md border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900";
const SETTING_SECTION_CARD =
  "h-full shadow-sm border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900";
const SETTING_SECTION_GRID =
  "grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5";
const COLLECTION_SETTING_SECTION_ORDER = ["identity", "organization", "visibility", "storage"] as const;

/** Shorter section intros on wizard step 3 to reduce repetition with field hints. */
const WIZARD_REVIEW_SECTION_DESCRIPTION: Record<string, string> = {
  organization:
    "Optional tenant scope. Managers with this organization on their account get item access without extra permission rows.",
  visibility: "Who can open the collection and whether group filters appear in the UI.",
  storage:
    "MongoDB collection name for this app's data. Renaming later can strand existing records—pick a stable name.",
};

function normalizeWizardSlug(raw: unknown): string {
  if (raw == null) return "";
  return String(raw)
    .replace(/[!]+$/, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function slugFromCollectionName(name: string): string {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function findWizardScopedSlugDuplicate(
  slugRaw: string,
  organizationForSave: string | undefined,
  currentUserUid: string | null,
  existing: any[],
  excludeUid: string | null | undefined
): { row: any; slugNorm: string } | null {
  const slugNorm = normalizeWizardSlug(slugRaw);
  if (!slugNorm) {
    return null;
  }
  const orgNorm = organizationForSave ? normalizeOrgSegment(organizationForSave) : "";
  for (const row of existing || []) {
    if (!row) continue;
    if (excludeUid && String(row.uid) === String(excludeUid)) {
      continue;
    }
    const rowSlugNorm = normalizeWizardSlug(row.slug || "");
    if (rowSlugNorm !== slugNorm) {
      continue;
    }
    const rOrg = normalizeOrgSegment(row.organization || "");
    if (orgNorm) {
      if (rOrg === orgNorm) {
        return { row, slugNorm };
      }
    } else if (rOrg === "") {
      const pb =
        currentUserUid != null && String(currentUserUid).trim() !== ""
          ? String(currentUserUid).trim()
          : "";
      const rawRb = (row as any).createdBy ?? (row as any)._createdBy;
      const rb =
        rawRb != null && String(rawRb).trim() !== "" ? String(rawRb).trim() : "";
      if (!pb && !rb) {
        return { row, slugNorm };
      }
      if (pb && rb && pb === rb) {
        return { row, slugNorm };
      }
      if (!pb || !rb) {
        return { row, slugNorm };
      }
    }
  }
  return null;
}

export type CollectionWizardChrome = {
  step: number;
  totalSteps: number;
  primaryLabel: string;
  showBack: boolean;
  nextDisabled?: boolean;
  nextDisabledReason?: string;
};

type CollectionWizardStepDef = {
  id: "identity" | "fields" | "organization" | "storage" | "review";
  label: string;
};

const EditComponent = forwardRef<
  { save: () => Promise<boolean>; hasInteracted: () => boolean; wizardBack?: () => void; requestCancel?: () => void },
  {
    onEditComponent: Function;
    onNewComponentCreated: Function;
    hideActionButtons?: boolean;
    onCollectionWizardUiChange?: (ui: CollectionWizardChrome | null) => void;
  }
>(({
  onEditComponent,
  onNewComponentCreated,
  hideActionButtons = false,
  onCollectionWizardUiChange,
}, ref) => {
  const [editField, setEditField] = useState<any>([]);
  const [showEditField, setShowEditField] = useState(false);
  const [componentFields, setComponentFields] = useState([]);
  const [formValues, setFormValues] = useState<any>({});
  const [initialCollectionValues, setInitialCollectionValues] = useState({
    mongoCollection: ''
  });
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const requestCancelRef = useRef<() => void>(() => { });
  const [showDeleteFieldConfirm, setShowDeleteFieldConfirm] = useState(false);
  const [pendingDeleteField, setPendingDeleteField] = useState<any>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [focusLabelOnNewField, setFocusLabelOnNewField] = useState(false);
  const [showImportFieldsDialog, setShowImportFieldsDialog] = useState(false);
  const [importCandidateFields, setImportCandidateFields] = useState<any[]>([]);
  const [importMode, setImportMode] = useState<'all' | 'select'>('all');
  const [selectedImportIndices, setSelectedImportIndices] = useState<Set<number>>(new Set());
  const [importError, setImportError] = useState<string | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [tenants, setTenants] = useState<TenantListItem[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantsError, setTenantsError] = useState<string | null>(null);
  const [newWizardStep, setNewWizardStep] = useState(1);
  const [wizardStep1IdentityWarning, setWizardStep1IdentityWarning] = useState<string | null>(null);
  const [editMainTab, setEditMainTab] = useState<"settings" | "fields">("settings");
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const componentIdentityRef = useRef<string | null>(null);
  const lastSuggestBaseRef = useRef<string | null>(null);
  const openEditFormForNewFieldRef = useRef(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const reduxUser = useSelector((state: any) => state.user);
  const tableMeta = useSelector((state: any) => state.table.meta ?? EMPTY_ARRAY);
  const { user: authUser } = useAuth();

  const componentData: IComponentData = useSelector((state: any) => state.component);
  const component: IComponent = componentData.current;
  const componentBackup = componentData.backup;
  const isNew = useSelector((state: any) => state.component.isNew);
  // Use auth context user if available, fallback to Redux user
  const user = authUser || reduxUser;
  const existingComponents = useSelector((state: any) => state.components.data ?? EMPTY_ARRAY);

  const isAdmin = Boolean(user?.admin ?? user?.info?.admin);

  const currentUserUidForWizard = useMemo(() => {
    const u = user?.uid || (user as any)?.info?.uid || (user as any)?.info?._id || (user as any)?.id || (user as any)?._id;
    return u != null && String(u).trim() !== "" ? String(u).trim() : null;
  }, [user]);

  const sessionOrganizationLabel = useMemo(() => {
    const raw = user?.organization ?? user?.info?.organization;
    return typeof raw === "string" ? raw.trim() : "";
  }, [user?.organization, user?.info?.organization]);

  /** Non-admin users with an organization on their profile must use that tenant only (required, not cleared). */
  const organizationRestrictedToSession =
    !isAdmin && sessionOrganizationLabel.length > 0;

  const collectionWizardSteps = useMemo<CollectionWizardStepDef[]>(() => {
    const steps: CollectionWizardStepDef[] = [
      { id: "identity", label: "Name & URL" },
      { id: "fields", label: "Fields" },
    ];
    if (!isOrganizationScopingDisabled() && !organizationRestrictedToSession) {
      steps.push({ id: "organization", label: "Organization" });
    }
    if (isAdmin) {
      steps.push({ id: "storage", label: "Storage" });
    }
    steps.push({ id: "review", label: "Review" });
    return steps;
  }, [organizationRestrictedToSession, isAdmin]);

  const wizardTotalSteps = collectionWizardSteps.length;
  const collectionWizardStepId = collectionWizardSteps[newWizardStep - 1]?.id;

  useEffect(() => {
    if (!isNew) return;
    setNewWizardStep((s) => {
      if (s > collectionWizardSteps.length) {
        return collectionWizardSteps.length;
      }
      if (s < 1) {
        return 1;
      }
      return s;
    });
  }, [isNew, collectionWizardSteps.length]);

  const collectionTargetId = useMemo(() => {
    const slug = typeof component?.slug === "string" ? component.slug.trim() : "";
    if (slug) return slug;
    const uid = typeof component?.uid === "string" ? component.uid.trim() : "";
    return uid || "";
  }, [component?.slug, component?.uid]);

  const editCollectionNameRequest = useMemo<PermissionCheckRequest | null>(() => {
    if (!collectionTargetId) return null;
    return {
      action: "edit_collection_name",
      targetType: "collection",
      targetId: collectionTargetId
    };
  }, [collectionTargetId]);

  // For new components, use '*' so we ask "can suggest name for any new collection?"
  // Using the suggested slug as targetId would deny when that slug is an existing collection.
  const editCollectionNameSuggestionRequest = useMemo<PermissionCheckRequest | null>(() => {
    if (isNew) {
      return {
        action: "edit_collection_name_suggestion",
        targetType: "collection",
        targetId: "*"
      };
    }
    if (!collectionTargetId) return null;
    return {
      action: "edit_collection_name_suggestion",
      targetType: "collection",
      targetId: collectionTargetId
    };
  }, [isNew, collectionTargetId]);

  const {
    allowed: canEditCollectionName
  } = usePermission(editCollectionNameRequest, {
    enabled: Boolean(collectionTargetId && !isNew)
  });

  const {
    allowed: canEditCollectionNameSuggestion
  } = usePermission(editCollectionNameSuggestionRequest, {
    enabled: isNew ? true : Boolean(collectionTargetId)
  });

  const viewCollectionAdvancedRequest = useMemo<PermissionCheckRequest | null>(() => ({
    action: "view_collection_advanced",
    targetType: "collection",
    targetId: "*"
  }), []);
  const { allowed: canViewCollectionAdvanced } = usePermission(viewCollectionAdvancedRequest, { enabled: true });

  const collectionRouteFromPath = useMemo(
    () => parseCollectionTablePath(location.pathname),
    [location.pathname]
  );

  const getFieldValueByKey = (fieldKey: keyof IComponent, defaultValue?: any) => {
    if (typeof component === 'undefined') {
      return defaultValue !== undefined && defaultValue !== null ? defaultValue : null;
    }
    const raw = component[fieldKey];
    if (raw === undefined || raw === null) {
      return defaultValue !== undefined && defaultValue !== null ? defaultValue : null;
    }
    return raw;
  };

  const normalizeOrganizationForSave = (org: unknown): string | undefined => {
    if (typeof org !== 'string') return undefined;
    const t = org.trim();
    return t.length > 0 ? t : undefined;
  };

  const settingsSectionHeaderIndex = useMemo(() => {
    const map = new Map<number, string>();
    let prev: string | undefined;
    componentDef.forEach((f: { section?: string }, i: number) => {
      if (f.section && f.section !== prev) {
        map.set(i, f.section);
        prev = f.section;
      }
    });
    return map;
  }, []);

  const firstSettingsSectionIndex = useMemo(() => {
    const keys = [...settingsSectionHeaderIndex.keys()];
    return keys.length ? Math.min(...keys) : null;
  }, [settingsSectionHeaderIndex]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setTenantsLoading(true);
        setTenantsError(null);
        const token =
          typeof localStorage !== "undefined"
            ? localStorage.getItem("authToken") || localStorage.getItem("idToken")
            : null;
        const list = await fetchTenantList({ token });
        if (!cancelled) {
          setTenants(list);
        }
      } catch (err) {
        if (!cancelled) {
          setTenantsError(err instanceof Error ? err.message : "Failed to load organizations");
          setTenants([]);
        }
      } finally {
        if (!cancelled) {
          setTenantsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lockedTenantRecord = useMemo(() => {
    if (!organizationRestrictedToSession) return null;
    return (
      tenants.find(
        (t) =>
          t.name.localeCompare(sessionOrganizationLabel, undefined, { sensitivity: "base" }) === 0
      ) ?? null
    );
  }, [organizationRestrictedToSession, sessionOrganizationLabel, tenants]);

  const lockedOrganizationValue =
    organizationRestrictedToSession && sessionOrganizationLabel.length > 0
      ? lockedTenantRecord?.name ?? sessionOrganizationLabel
      : null;

  const organizationForSave = useMemo(() => {
    if (isOrganizationScopingDisabled()) {
      if (!isNew) {
        return normalizeOrganizationForSave(component.organization);
      }
      return undefined;
    }
    if (organizationRestrictedToSession && lockedOrganizationValue) {
      const t = lockedOrganizationValue.trim();
      return t.length > 0 ? t : undefined;
    }
    return normalizeOrganizationForSave(component.organization);
  }, [isNew, organizationRestrictedToSession, lockedOrganizationValue, component.organization]);

  const runWizardStep1DuplicateCheck = useCallback(
    (trigger: string, blurField?: "name" | "slug", blurRaw?: string) => {
      console.log("[EditComponent wizard] duplicate check", {
        trigger,
        blurField,
        blurRawPreview: typeof blurRaw === "string" ? blurRaw.slice(0, 120) : blurRaw,
        isNew,
        newWizardStep,
        organizationForSave,
        slugFromRedux: component?.slug,
      });
      if (!isNew || newWizardStep !== 1) {
        setWizardStep1IdentityWarning(null);
        return;
      }
      let slugNorm = "";
      if (blurField === "name" && blurRaw != null) {
        const fromName = slugFromCollectionName(blurRaw);
        slugNorm = normalizeWizardSlug(fromName || (component.slug as string) || "");
      } else if (blurField === "slug" && blurRaw != null) {
        slugNorm = normalizeWizardSlug(blurRaw);
      } else {
        slugNorm = normalizeWizardSlug(component.slug || "");
      }
      if (!slugNorm) {
        console.log("[EditComponent wizard] duplicate check skipped: empty normalized slug");
        setWizardStep1IdentityWarning(null);
        return;
      }
      const dup = findWizardScopedSlugDuplicate(
        slugNorm,
        organizationForSave,
        currentUserUidForWizard,
        existingComponents,
        isNew ? null : component?.uid
      );
      if (dup) {
        const msg = `This URL slug (“${dup.slugNorm}”) is already used by “${dup.row.name || dup.row.slug || "another collection"}”. Use a different name or slug.`;
        console.warn("[EditComponent wizard] duplicate slug in scope", {
          trigger,
          slugNorm: dup.slugNorm,
          conflictUid: dup.row.uid,
          conflictName: dup.row.name,
          conflictSlug: dup.row.slug,
          organizationForSave,
          currentUserUid: currentUserUidForWizard,
        });
        setWizardStep1IdentityWarning(msg);
      } else {
        console.log("[EditComponent wizard] no duplicate for normalized slug", { trigger, slugNorm });
        setWizardStep1IdentityWarning(null);
      }
    },
    [
      isNew,
      newWizardStep,
      component.slug,
      component?.uid,
      organizationForSave,
      currentUserUidForWizard,
      existingComponents,
    ]
  );

  useEffect(() => {
    if (!isNew || newWizardStep !== 1) return;
    runWizardStep1DuplicateCheck("identity-deps");
  }, [isNew, newWizardStep, organizationForSave, component.slug, existingComponents, runWizardStep1DuplicateCheck]);

  useEffect(() => {
    if (isOrganizationScopingDisabled()) return;
    if (!organizationRestrictedToSession || !lockedOrganizationValue) return;
    const current = typeof component.organization === "string" ? component.organization.trim() : "";
    if (
      !current ||
      current.localeCompare(lockedOrganizationValue, undefined, { sensitivity: "base" }) !== 0
    ) {
      dispatch({
        type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED,
        payload: { key: "organization", value: lockedOrganizationValue },
      });
    }
  }, [
    organizationRestrictedToSession,
    lockedOrganizationValue,
    component.organization,
    dispatch,
  ]);

  useEffect(() => {
    if (!onCollectionWizardUiChange) return;
    if (!isNew) {
      onCollectionWizardUiChange(null);
      return;
    }
    onCollectionWizardUiChange({
      step: newWizardStep,
      totalSteps: wizardTotalSteps,
      primaryLabel: collectionWizardStepId === "review" ? "Create collection" : "Next",
      showBack: newWizardStep > 1,
      nextDisabled: collectionWizardStepId === "identity" && Boolean(wizardStep1IdentityWarning),
      nextDisabledReason: wizardStep1IdentityWarning || undefined,
    });
  }, [
    isNew,
    newWizardStep,
    wizardTotalSteps,
    collectionWizardStepId,
    onCollectionWizardUiChange,
    wizardStep1IdentityWarning,
  ]);

  const generateFieldName = (labelValue: string) =>
    labelValue.toLowerCase().replace(/\s+/g, '_');

  const normalizeComponentFields = (fields: any[] = []) => {
    return fields.map((field: any) => {
      const currentName = typeof field?.name === 'string' ? field.name.trim() : '';
      if (currentName) {
        return field;
      }
      const keyValue = typeof field?.key === 'string' ? field.key.trim() : '';
      if (keyValue) {
        return { ...field, name: keyValue };
      }
      const labelValue = typeof field?.label === 'string' ? field.label.trim() : '';
      if (labelValue) {
        return { ...field, name: generateFieldName(labelValue) };
      }
      if (field?.uid) {
        return { ...field, name: `field_${String(field.uid).replace(/[^a-z0-9_]/gi, '')}` };
      }
      return field;
    });
  };

  const handleUpdateError = (message?: string) => {
    const fallback = typeof message === 'string' && message.trim().length > 0
      ? message.trim()
      : 'Unable to save component configuration. Please try again.';
    // Replace newlines with spaces for better display in Alert
    const formattedMessage = fallback.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    console.log('🔴 Setting updateError:', formattedMessage);
    setUpdateError(formattedMessage);
  };

  const revertComponentToBackup = () => {
    if (isNew) {
      console.warn('Skipping component revert for drafts - no saved snapshot available.');
      return;
    }

    if (!componentBackup) {
      console.warn('No backup available to reset the component form.');
      return;
    }

    const restored = JSON.parse(JSON.stringify(componentBackup));
    setComponentFields(restored.fields || []);
    setFormValues(restored);
    dispatch({ type: ActionTypes.EDIT_COMPONENT_RESET });
    setHasInteracted(false);
  };

  const handleSaveFailure = (message?: string) => {
    handleUpdateError(message);
    revertComponentToBackup();
  };


  useEffect(() => {
    const fields = getFieldValueByKey('fields', []);
    setComponentFields(fields);
    setFormValues(component);
    if (openEditFormForNewFieldRef.current && fields.length > 0) {
      openEditFormForNewFieldRef.current = false;
      setEditField(fields[fields.length - 1]);
      setShowEditField(true);
      setFocusLabelOnNewField(true);
    } else {
      setHasInteracted(false);
    }

    // Debug logging to see what's being loaded
    console.log('Component loaded in editor:', {
      dataSource: component.dataSource,
      mongoCollection: component.mongoCollection
    });
  }, [component]);

  useEffect(() => {
    const identity = isNew
      ? `new-${component?.uid || 'draft'}`
      : component?.uid || 'unknown';

    if (componentIdentityRef.current === identity) {
      return;
    }

    componentIdentityRef.current = identity;
    // Only clear error if we're not in the middle of a save operation
    // This prevents the error from being cleared when the component is reverted after a failed save
    if (!isSaving) {
      setUpdateError(null);
    }
  }, [component?.uid, isNew, isSaving]);

  useEffect(() => {
    const mongoInitial = component?.mongoCollection || '';
    setInitialCollectionValues({
      mongoCollection: mongoInitial
    });

    console.log('Collection lock snapshot:', {
      mongoInitial,
      isNew
    });
  }, [componentData.backup, isNew]);



  const updateFormField = (e: any) => {
    const { name, value } = e.target;
    console.log('🔄 updateFormField called:', { name, value });
    dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: name, value: value } })
  };

  // Handle input change for all fields
  const handleInputChange = (e: { target: { name: any; value: any } }, field: any) => {
    setHasInteracted(true);
    if (isNew && collectionWizardStepId === "identity" && (field.key === "name" || field.key === "slug")) {
      setWizardStep1IdentityWarning(null);
    }
    let { name, value } = e.target;
    switch (field.case) {
      case 'lower':
        value = value.toLowerCase();
        break;
      case 'title':
        value = value.charAt(0).toUpperCase() + value.slice(1);
        break;
      default:
        value = value;
    }

    if (field.key === 'name' && field.copyTo === 'slug' && isNew) {
      suggestSlugAndCollection(value);
    } else if (field.copyTo && isNew) {
      const copyTo = field.copyTo;
      if (typeof copyTo !== 'undefined') {
        let copyValue: string;
        if (field.copyCase === 'slug') {
          copyValue = String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || '';
        } else {
          switch (field.copyCase) {
            case 'lower':
              copyValue = value.toLowerCase();
              break;
            case 'title':
              copyValue = value.charAt(0).toUpperCase() + value.slice(1);
              break;
            default:
              copyValue = value.toLowerCase();
          }
        }
        if (copyValue !== undefined) {
          console.log('Dispatching copy action:', { key: copyTo, value: copyValue });
          dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: copyTo, value: copyValue } });
        }
      }
    }
    console.log('Dispatching update action:', { key: name, value: value });
    dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: name, value: value } })
  }

  const handleCheckboxChange = (field: any, checked: boolean) => {
    setHasInteracted(true);
    dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: field.key, value: checked } })
  };


  const addField = () => {
    setHasInteracted(true);
    if (componentData) {
      dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_NEW });
      openEditFormForNewFieldRef.current = true;
    }
  };

  const canAdvanceWizardFromStep = (stepNum: number): boolean => {
    if (stepNum !== 1) return true;
    const nameOk = (component?.name || "").trim().length > 0;
    const slugOk = (component?.slug || "").trim().length > 0;
    if (!nameOk || !slugOk) return false;
    if (organizationRestrictedToSession && !(organizationForSave || "").trim()) return false;
    if (wizardStep1IdentityWarning) return false;
    return true;
  };

  const validateLeavingWizardStep = (stepNum: number): boolean => {
    setUpdateError(null);
    if (stepNum !== 1) return true;
    const nameOk = (component?.name || "").trim().length > 0;
    const slugOk = (component?.slug || "").trim().length > 0;
    if (!nameOk) {
      toast.error("Enter a collection name to continue.");
      return false;
    }
    if (!slugOk) {
      toast.error("Add a URL slug (or type a name so one can be generated).");
      return false;
    }
    if (organizationRestrictedToSession && !(organizationForSave || "").trim()) {
      toast.error("Your account requires an organization. Wait for organizations to load, then try again.");
      return false;
    }
    if (wizardStep1IdentityWarning) {
      toast.error("Fix the duplicate URL slug before continuing.");
      return false;
    }
    return true;
  };

  const ensureAutoMongoCollectionNonAdmin = () => {
    if (isAdmin || !isNew) return;
    const slug = normalizeWizardSlug(component.slug);
    if (!slug) return;
    const current = String(component.mongoCollection || "").trim();
    if (current) return;
    dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: "mongoCollection", value: slug } });
    const excludeUid = isNew ? null : (component?.uid || undefined);
    dataAPI
      .suggestUniqueCollectionName(slug, excludeUid)
      .then((res) => {
        const suggestedName = res?.data?.suggestedName;
        if (typeof suggestedName === "string" && suggestedName) {
          dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: "mongoCollection", value: suggestedName } });
        }
      })
      .catch(() => { });
  };

  const goToWizardStep = (n: number) => {
    if (n < newWizardStep) {
      setNewWizardStep(n);
      return;
    }
    if (n === newWizardStep + 1 && newWizardStep < wizardTotalSteps && canAdvanceWizardFromStep(newWizardStep)) {
      if (validateLeavingWizardStep(newWizardStep)) {
        const nextId = collectionWizardSteps[n - 1]?.id;
        if (!isAdmin && nextId === "review") {
          ensureAutoMongoCollectionNonAdmin();
        }
        setNewWizardStep(n);
      }
    }
  };

  const tryAdvanceNewWizardOrSave = async (): Promise<boolean> => {
    if (!isNew) {
      return handleAddComponent();
    }
    if (newWizardStep < wizardTotalSteps) {
      if (!validateLeavingWizardStep(newWizardStep)) {
        return false;
      }
      const nextStep = newWizardStep + 1;
      const nextId = collectionWizardSteps[nextStep - 1]?.id;
      if (!isAdmin && nextId === "review") {
        ensureAutoMongoCollectionNonAdmin();
      }
      setNewWizardStep(nextStep);
      return false;
    }
    if (!isAdmin) {
      ensureAutoMongoCollectionNonAdmin();
    }
    return handleAddComponent();
  };

  const deleteField = (uid: string) => {
    dispatch({ type: ActionTypes.EDIT_COMPONENT_FIELD_DELETE, payload: { uid: uid } })
    dispatch({ type: ActionTypes.EDIT_COMPONENT_UPDATE_STATE, payload: componentData })
    componentUpdate(componentData.current)
    setShowEditField(false);
    setFocusLabelOnNewField(false);
  };

  const requestDeleteField = (field: any) => {
    if (!field?.uid) {
      return;
    }
    setPendingDeleteField(field);
    setShowDeleteFieldConfirm(true);
  };

  const handleConfirmDeleteField = () => {
    if (pendingDeleteField?.uid) {
      deleteField(pendingDeleteField.uid);
    }
    setShowDeleteFieldConfirm(false);
    setPendingDeleteField(null);
  };

  const generateFieldUid = () =>
    Math.random().toString(36).substring(2, 8);

  const exportFieldsAsJson = () => {
    const fields = getFieldValueByKey('fields', []) as any[];
    const payload = {
      componentName: component?.name ?? '',
      slug: component?.slug ?? '',
      exportedAt: new Date().toISOString(),
      fields: fields.map((f: any) => ({ ...f })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const name = (component?.slug || component?.name || 'component').replace(/[^a-z0-9_-]/gi, '_');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}-fields.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseImportedFields = (json: string): any[] | null => {
    try {
      const data = JSON.parse(json);
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && Array.isArray(data.fields)) return data.fields;
      return null;
    } catch {
      return null;
    }
  };

  const openImportDialog = () => {
    setImportError(null);
    setImportCandidateFields([]);
    setImportMode('all');
    setSelectedImportIndices(new Set());
    importFileInputRef.current?.click();
  };

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const fields = parseImportedFields(text);
      if (!fields || fields.length === 0) {
        setImportError('Invalid or empty file. Expected JSON with a "fields" array or a JSON array of field objects.');
        setShowImportFieldsDialog(true);
        setImportCandidateFields([]);
        return;
      }
      setImportError(null);
      setImportCandidateFields(fields);
      setSelectedImportIndices(new Set(fields.map((_, i) => i)));
      setShowImportFieldsDialog(true);
    };
    reader.readAsText(file);
  };

  const toggleImportFieldSelection = (index: number) => {
    setSelectedImportIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectAllImportFields = () => {
    setSelectedImportIndices(new Set(importCandidateFields.map((_, i) => i)));
  };

  const selectNoneImportFields = () => {
    setSelectedImportIndices(new Set());
  };

  const applyImportedFields = () => {
    const currentFields = (getFieldValueByKey('fields', []) as any[]) || [];
    const toAdd =
      importMode === 'all'
        ? importCandidateFields
        : importCandidateFields.filter((_, i) => selectedImportIndices.has(i));
    if (toAdd.length === 0) {
      setImportError('Select at least one field to import.');
      return;
    }
    const baseOrder = currentFields.length;
    const newFields = toAdd.map((f: any, i: number) => ({
      ...f,
      uid: generateFieldUid(),
      order: baseOrder + i,
    }));
    const merged = normalizeComponentFields([...currentFields, ...newFields]).map((f: any, i: number) => ({
      ...f,
      uid: f.uid || generateFieldUid(),
      order: i,
    }));
    setHasInteracted(true);
    dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: 'fields', value: merged } });
    setComponentFields(merged);
    setShowImportFieldsDialog(false);
    setImportCandidateFields([]);
    setImportError(null);
  };

  const handleAddComponent = async (): Promise<boolean> => {
    setUpdateError(null);
    setIsSaving(true);
    let apiComponent: { [key: string]: any } = { ...componentData };
    if (isNew) {
      try {
        apiComponent.current = {
          ...apiComponent.current,
          fields: normalizeComponentFields(apiComponent.current?.fields || []),
          organization: organizationForSave,
        };
        console.log('apiComponent.current:', apiComponent.current)
        console.log('Required fields check:', {
          public: apiComponent.current.public,
          dataSource: apiComponent.current.dataSource,
          mongoCollection: apiComponent.current.mongoCollection
        })
        const response = await dataAPI.createComponent(apiComponent.current);
        console.log('🔍 Create component response:', response);
        const created = response.data as
          | { uid?: string; id?: string; mongoCollection?: string; organization?: string | null; slug?: string }
          | undefined;
        const componentId = created?.uid || created?.id;
        if (!componentId) {
          const msg = 'Collection was created but the server did not return a component id. Reload the page to continue.';
          setUpdateError(msg);
          handleSaveFailure(msg);
          return false;
        }

        const storage = (response as { storage?: { collection?: string } }).storage;
        const mongoFromServer =
          (typeof created?.mongoCollection === 'string' && created.mongoCollection.trim()) ||
          (typeof storage?.collection === 'string' && storage.collection.trim()) ||
          '';
        const mongoCollectionResolved =
          mongoFromServer || `${apiComponent.current.slug}_${Math.random().toString(36).substring(7)}`;

        dispatch({
          type: ActionTypes.EDIT_COMPONENT_SET_META,
          payload: { uid: componentId, collection: mongoCollectionResolved },
        });
        dispatch({ type: ActionTypes.EDIT_COMPONENT_START, payload: apiComponent.current });
        componentData.current.uid = componentId;
        componentData.current.mongoCollection = mongoCollectionResolved;

        // Persist uid and server Mongo collection name (create already wrote the component; this aligns the client payload)
        try {
          const postCreateResponse = await componentUpdate({
            ...apiComponent.current,
            uid: componentId,
            mongoCollection: mongoCollectionResolved,
          });
          if (postCreateResponse?.success === false) {
            console.error('❌ Post-creation component update reported success=false:', postCreateResponse);
            handleSaveFailure(postCreateResponse.message);
            return false;
          }
        } catch (updateError) {
          console.error('❌ Post-creation component update threw an error:', updateError);
          let errorMessage: string | undefined;
          if (updateError instanceof Error) {
            errorMessage = updateError.message;
          } else if (typeof updateError === 'string') {
            errorMessage = updateError;
          } else if (updateError && typeof updateError === 'object') {
            errorMessage = (updateError as any).message || (updateError as any).error || JSON.stringify(updateError);
          } else {
            errorMessage = 'Failed to update component after creation. Please try again.';
          }
          const formattedError = errorMessage.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
          setUpdateError(formattedError);
          handleSaveFailure(errorMessage);
          return false;
        }
        invalidateAllCachesUsedByFetchMeta();
        // Use server component for navigation so URL org segment matches persisted organization (avoids table key mismatch / endless "Loading data" on /:org/:slug)
        const navPayload = {
          ...apiComponent.current,
          ...(created && typeof created === 'object' ? created : {}),
          uid: componentId,
          mongoCollection: mongoCollectionResolved,
        };
        onNewComponentCreated(navPayload);
        // Close editor on successful save
        onEditComponent(false);
        return true;
      } catch (error) {
        console.error("Error creating component: ", error);
        let errorMessage: string | undefined;

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && typeof error === 'object') {
          errorMessage = (error as any).message || (error as any).error || JSON.stringify(error);
        } else {
          errorMessage = 'Failed to create component. Please try again.';
        }

        // Set error directly to ensure it's set
        const formattedError = errorMessage.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
        setUpdateError(formattedError);
        console.log('🔴 Directly set updateError to (create path):', formattedError);

        // Also call handleSaveFailure for toast and other handling
        handleSaveFailure(errorMessage);
        return false;
      } finally {
        setIsSaving(false);
      }
    } else {
      // Use the current component from Redux state for updates
      console.log('Updating component with data:', component);
      console.log('Component data from Redux:', componentData.current);
      try {
        console.log('🔄 About to save component with dataSource:', component.dataSource);
        const normalizedComponent = {
          ...component,
          organization: organizationForSave,
          fields: normalizeComponentFields(component.fields || [])
        };
        if (normalizedComponent.fields !== component.fields) {
          dispatch({
            type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED,
            payload: { key: 'fields', value: normalizedComponent.fields }
          });
        }
        const updateResponse = await componentUpdate(normalizedComponent);
        if (updateResponse?.success === false) {
          console.error('❌ Component configuration update responded with success=false:', updateResponse);
          handleSaveFailure(updateResponse.message);
          return false;
        }
        // Update the component in the list with the latest data
        dispatch({ type: ActionTypes.COMPONENT_UPDATED_IN_LIST, payload: normalizedComponent });
        console.log('✅ Component updated successfully and list updated');
        // Close editor on successful save
        onEditComponent(false);
        return true;
      } catch (error) {
        console.error('❌ Failed to update component:', error);
        let errorMessage: string | undefined;

        if (error instanceof Error) {
          errorMessage = error.message;
          console.log('🔴 Error is Error instance, message:', errorMessage);
        } else if (typeof error === 'string') {
          errorMessage = error;
          console.log('🔴 Error is string:', errorMessage);
        } else if (error && typeof error === 'object') {
          errorMessage = (error as any).message || (error as any).error || JSON.stringify(error);
          console.log('🔴 Error is object, extracted:', errorMessage);
        } else {
          errorMessage = 'Failed to update component. Please try again.';
          console.log('🔴 Error is unknown type, using fallback');
        }

        console.log('🔴 Final error message to display:', errorMessage);
        console.log('🔴 Calling handleSaveFailure with:', errorMessage);

        // Set error directly to ensure it's set
        const formattedError = errorMessage.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
        setUpdateError(formattedError);
        console.log('🔴 Directly set updateError to:', formattedError);

        // Also call handleSaveFailure for toast and other handling
        handleSaveFailure(errorMessage);
        console.log('🔴 After handleSaveFailure, updateError state should be set');
        return false;
      } finally {
        setIsSaving(false);
      }
    }
  };

  const saveHandlerRef = useRef(tryAdvanceNewWizardOrSave);
  saveHandlerRef.current = tryAdvanceNewWizardOrSave;

  useImperativeHandle(ref, () => ({
    save: () => saveHandlerRef.current(),
    wizardBack: () => {
      setNewWizardStep((s) => (s > 1 ? s - 1 : s));
    },
    hasInteracted: () => hasInteracted,
    requestCancel: () => requestCancelRef.current(),
  }));


  const handleOnNewField = () => {
    // setComponentData(componentData);
  };


  const handleFormFieldsSort = (fields: any) => {
    setHasInteracted(true);
    // setComponentData({
    //   ...componentData,
    //   fields,
    // });
    dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: 'fields', value: fields } })

  };

  const handleChangeFormFieldToEdit = (e: any) => {
    setHasInteracted(true);
    setEditField(e);
    setShowEditField(true);
  };

  const reloadConfigAndData = () => {
    if (user) {
      reloadAppConfig(dispatch, user, {
        reloadCurrentCollection: Boolean(collectionRouteFromPath),
        pathname: location.pathname,
        tableMeta,
      });
    }
  };

  const handleCancelClick = () => {
    if (hasInteracted) {
      setShowCancelConfirm(true);
    } else {
      // Reset component to backup if it exists (only for existing components, not new ones)
      if (!isNew) {
        dispatch({ type: ActionTypes.EDIT_COMPONENT_RESET });
      }
      // Reset local state to ensure fresh values on next edit
      setFormValues({});
      setComponentFields([]);
      setHasInteracted(false);
      onEditComponent(false);
      reloadConfigAndData();
    }
  };
  requestCancelRef.current = handleCancelClick;

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    // Reset component to backup if it exists (only for existing components, not new ones)
    if (!isNew) {
      dispatch({ type: ActionTypes.EDIT_COMPONENT_RESET });
    }
    // Reset local state to ensure fresh values on next edit
    setFormValues({});
    setComponentFields([]);
    setHasInteracted(false);
    onEditComponent(false);
    reloadConfigAndData();
  };

  const handleEditorInteraction = () => {
    setHasInteracted(true);
  };



  const handleOnFieldUpdated = () => {
    setShowEditField(false);
    setFocusLabelOnNewField(false);
  };

  useEffect(() => {
    if (!showEditField) setFocusLabelOnNewField(false);
  }, [showEditField]);

  useEffect(() => {
    if (!isNew && showEditField) {
      setEditMainTab("fields");
    }
  }, [isNew, showEditField]);

  useEffect(() => {
    if (!user) return;
    const userId = user?.uid || (user as { info?: { uid?: string } })?.info?.uid;
    if (!userId) return;
    if (!existingComponents.length) {
      dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user } });
    }
  }, [user, existingComponents.length, dispatch]);

  useEffect(() => {
    if (!showEditField || !user) return;
    dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user } });
  }, [showEditField, user, dispatch]);

  const suggestSlugAndCollection = (sourceName: string) => {
    const baseSlug = String(sourceName || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (!baseSlug) return;
    dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: 'slug', value: baseSlug } });
    dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: 'mongoCollection', value: baseSlug } });
    setHasInteracted(true);
    lastSuggestBaseRef.current = baseSlug;
    const excludeUid = isNew ? null : (component?.uid || undefined);
    dataAPI.suggestUniqueCollectionName(baseSlug, excludeUid)
      .then((res) => {
        const suggestedName = res?.data?.suggestedName;
        if (typeof suggestedName === 'string' && suggestedName && lastSuggestBaseRef.current === baseSlug) {
          dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: 'mongoCollection', value: suggestedName } });
        }
      })
      .catch(() => { });
  };

  const suggestMongoCollectionName = () => {
    const slug = (component.slug || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (!slug) return;
    const excludeUid = isNew ? null : (component?.uid || undefined);
    dataAPI.suggestUniqueCollectionName(slug, excludeUid)
      .then((res) => {
        const suggestedName = res?.data?.suggestedName;
        if (typeof suggestedName === 'string' && suggestedName) {
          dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: 'mongoCollection', value: suggestedName } });
        } else {
          dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: 'mongoCollection', value: slug } });
        }
      })
      .catch(() => {
        dispatch({ type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED, payload: { key: 'mongoCollection', value: slug } });
      });
    setHasInteracted(true);
  };

  const getFieldIcon = (fieldKey: string) => {
    switch (fieldKey) {
      case 'name':
        return <Type className="w-4 h-4 text-blue-500" />
      case 'slug':
        return <Hash className="w-4 h-4 text-purple-500" />
      case 'mongoCollection':
        return <Database className="w-4 h-4 text-green-500" />
      case 'dataSource':
        return <Database className="w-4 h-4 text-indigo-500" />
      case 'organization':
        return <Building2 className="w-4 h-4 text-amber-600" />
      case 'showGroups':
        return <Type className="w-4 h-4 text-slate-600" />
      case 'menuIcon':
        return <LayoutGrid className="w-4 h-4 text-violet-600" />
      default:
        return <Type className="w-4 h-4 text-gray-500" />
    }
  };

  const generateField =
    (field: any, value: number, index: number, opts?: { inline?: boolean }) => {
      const inline = opts?.inline;
      const settingFieldShell = (key: string, content: React.ReactNode, cardClass?: string) =>
        inline ? (
          <div key={key} className={SETTING_FIELD_STACK}>
            {content}
          </div>
        ) : (
          <Card key={key} className={cardClass ?? SETTING_FIELD_CARD}>
            <CardContent className={SETTING_FIELD_CARD_CONTENT}>{content}</CardContent>
          </Card>
        );

      switch (field.type) {
        case "text":
          // Special handling for collection fields - MongoDB collection name is ACL protected
          if (field.key === 'mongoCollection') {
            const fieldValue = component[field.key as keyof IComponent] || '';
            const initialValue = initialCollectionValues.mongoCollection || '';
            const hasInitialValue = initialValue && initialValue.trim() !== '';
            const isExistingCollection = !isNew;

            const isReadOnly = isExistingCollection
              ? (hasInitialValue && !canEditCollectionName)
              : !canEditCollectionNameSuggestion;

            const canSuggest = !isReadOnly && (component.slug || '').trim().length > 0;

            return settingFieldShell(
              `${field.key}-${index}`,
              <>
                <div className="flex items-center space-x-2">
                  {getFieldIcon(field.key)}
                  <Label htmlFor={field.key} className={SETTING_FIELD_LABEL}>
                    {field.label}
                  </Label>
                  {canSuggest && (
                    <ShadcnButton
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={suggestMongoCollectionName}
                      className="shrink-0"
                    >
                      Suggest
                    </ShadcnButton>
                  )}
                  <DebugFieldButton
                    fieldConfig={field}
                    fieldValue={fieldValue}
                    label={field.label}
                  />
                </div>
                <TextField
                  fullWidth
                  name={field.key}
                  value={fieldValue}
                  onChange={isReadOnly ? undefined : (event) => { handleInputChange(event, field) }}
                  variant="outlined"
                  className="bg-white"
                  size="small"
                  InputProps={{
                    readOnly: Boolean(isReadOnly),
                    style: {
                      backgroundColor: isReadOnly ? '#f3f4f6' : 'white',
                      color: isReadOnly ? '#6b7280' : 'inherit'
                    }
                  }}
                  helperText={
                    isReadOnly
                      ? "Collection name changes are restricted for your account."
                      : isNew && collectionWizardStepId === "storage"
                        ? "Database collection for items in this app. Use Suggest if you want a unique name from the slug."
                        : "Enter the MongoDB collection name for this component. This will be used to store and retrieve data from MongoDB."
                  }
                  placeholder="e.g., kayak_bookings, pages, etc."
                />
              </>
            );
          }

          switch (field.case) {
            case 'lower':
              return settingFieldShell(
                `${field.key}-${index}`,
                <>
                  <div className="flex items-center space-x-2">
                    {getFieldIcon(field.key)}
                    <Label htmlFor={field.key} className={SETTING_FIELD_LABEL}>
                      {field.label}
                    </Label>
                    <DebugFieldButton
                      fieldConfig={field}
                      fieldValue={value}
                      label={field.label}
                    />
                  </div>
                  <TextField
                    fullWidth
                    name={field.key}
                    value={value}
                    onChange={(event) => { handleInputChange(event, field) }}
                    onBlur={
                      isNew && collectionWizardStepId === "identity" && field.key === "slug"
                        ? (e) => runWizardStep1DuplicateCheck("blur:slug", "slug", e.target.value)
                        : undefined
                    }
                    inputProps={{ style: { textTransform: 'lowercase' } }}
                    variant="outlined"
                    className="bg-white"
                    size="small"
                    helperText={typeof field.hint === 'string' ? field.hint : undefined}
                  />
                </>
              );
            case 'title':
              return settingFieldShell(
                String(index),
                <>
                  <div className="flex items-center space-x-2">
                    {getFieldIcon(field.key)}
                    <Label htmlFor={field.key} className={SETTING_FIELD_LABEL}>
                      {field.label}
                    </Label>
                    <DebugFieldButton
                      fieldConfig={field}
                      fieldValue={value}
                      label={field.label}
                    />
                  </div>
                  <TextField
                    fullWidth
                    name={field.key}
                    value={value}
                    onChange={(event) => { handleInputChange(event, field) }}
                    onBlur={
                      isNew && collectionWizardStepId === "identity" && field.key === "name"
                        ? (e) => runWizardStep1DuplicateCheck("blur:name", "name", e.target.value)
                        : undefined
                    }
                    inputProps={{ style: { textTransform: 'capitalize' } }}
                    variant="outlined"
                    className="bg-white"
                    size="small"
                    helperText={typeof field.hint === 'string' ? field.hint : undefined}
                  />
                </>
              );
            default:
              return settingFieldShell(
                `${field.key}-${index}`,
                <>
                  <div className="flex items-center space-x-2">
                    {getFieldIcon(field.key)}
                    <Label htmlFor={field.key} className={SETTING_FIELD_LABEL}>
                      {field.label}
                    </Label>
                    <DebugFieldButton
                      fieldConfig={field}
                      fieldValue={getFieldValueByKey(field.key)}
                      label={field.label}
                    />
                  </div>
                  <TextField
                    fullWidth
                    name={field.key}
                    value={getFieldValueByKey(field.key) ?? ''}
                    onChange={(event) => { handleInputChange(event, field) }}
                    variant="outlined"
                    className="bg-white"
                    size="small"
                    helperText={typeof field.hint === 'string' ? field.hint : undefined}
                  />
                </>
              );

          }
        // case "user":
        //   return (
        //     <div key={field.key} className="mb-2">
        //       <InputLabel id="select-label">{field.label}</InputLabel>
        //       {/* <Select
        //         fullWidth
        //         label={field.label}
        //         name={field.key}
        //         value={user}
        //         onChange={handleUserChange}
        //       > */}
        //         {/* <Option value="none">None</Option>
        //         {users.map((user, index) => (
        //           <Option key={index} value={user.uid}>
        //             {user.email}
        //           </Option>
        //         ))}
        //       </Select> */}
        //     </div>
        //   );
        case "select":
          // Use the current Redux state directly instead of getSelectValue
          const selectValue = component[field.key as keyof IComponent] || '';
          console.log(`Rendering select field ${field.key}:`, {
            selectValue,
            formValue: formValues[field.key],
            componentValue: component[field.key as keyof IComponent],
            componentData: componentData.current[field.key as keyof IComponent]
          });

          return (
            <div key={`${field.key}-${index}`} className="mb-8" ref={dropdownContainerRef}>
              <div className="flex items-center space-x-2 mb-3">
                {getFieldIcon(field.key)}
                <Label className={SETTING_FIELD_LABEL}>
                  {field.label}
                </Label>
                <DebugFieldButton
                  fieldConfig={field}
                  fieldValue={selectValue}
                  label={field.label}
                />
              </div>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel
                  id={`${field.key}-label`}
                  shrink={true}
                  sx={{
                    backgroundColor: 'white',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    fontSize: '0.8125rem',
                    transform: 'translate(14px, -9px) scale(0.75)',
                    '&.Mui-focused': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                    }
                  }}
                >
                  {field.label}
                </InputLabel>
                <Select
                  labelId={`${field.key}-label`}
                  name={field.key}
                  value={selectValue}
                  onChange={updateFormField}
                  defaultValue={''}
                  variant="outlined"
                  className="bg-white"
                  onOpen={(e) => {
                    e.stopPropagation();
                  }}
                  onClose={(e) => {
                    e.stopPropagation();
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 99999,
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    },
                    disablePortal: false,
                    disableAutoFocusItem: true,
                    disableEnforceFocus: true,
                    disableRestoreFocus: true,
                    container: dropdownContainerRef.current || document.body
                  }}
                >
                  {field.value.split(",").map((option: string, index: number) => {
                    const [value, label] = option.includes(':') ? option.split(':') : [option, option];
                    return (
                      <Option key={`${field.key}-${index}`} value={value}>
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
          );
        case "tenantSelect": {
          const orgRaw = getFieldValueByKey(field.key as keyof IComponent);
          const orgStr = typeof orgRaw === "string" ? orgRaw : "";
          const optionsList: TenantListItem[] = organizationRestrictedToSession
            ? lockedTenantRecord
              ? [lockedTenantRecord]
              : lockedOrganizationValue
                ? [{ uid: "__session_org", name: lockedOrganizationValue }]
                : []
            : tenants;
          const knownNames = new Set(optionsList.map((t) => t.name));
          const orphanLabel =
            !organizationRestrictedToSession && orgStr.trim() && !knownNames.has(orgStr);
          const selectValue = organizationRestrictedToSession
            ? (lockedOrganizationValue ?? orgStr)
            : orgStr.trim() === ""
              ? "__none"
              : orgStr;
          const selectDisabled = tenantsLoading || organizationRestrictedToSession;

          return settingFieldShell(
            `${field.key}-${index}`,
            <>
              <div className="flex items-center space-x-2">
                {getFieldIcon(field.key)}
                <Label htmlFor={`${field.key}-tenant`} className={SETTING_FIELD_LABEL}>
                  {field.label}
                  {organizationRestrictedToSession ? (
                    <span className="text-red-600 font-normal"> *</span>
                  ) : null}
                </Label>
                <DebugFieldButton
                  fieldConfig={field}
                  fieldValue={orgStr}
                  label={field.label}
                />
              </div>
              <ShadcnSelect
                value={selectValue}
                onValueChange={(v) => {
                  if (organizationRestrictedToSession || selectDisabled) return;
                  setHasInteracted(true);
                  dispatch({
                    type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED,
                    payload: { key: field.key, value: v === "__none" ? "" : v },
                  });
                }}
                disabled={selectDisabled}
              >
                <SelectTrigger
                  id={`${field.key}-tenant`}
                  className={SETTING_SELECT_TRIGGER}
                >
                  <SelectValue
                    placeholder={
                      tenantsLoading
                        ? "Loading organizations…"
                        : organizationRestrictedToSession
                          ? selectValue || "Organization"
                          : "No organization"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="z-[100002]" position="popper" sideOffset={4}>
                  {organizationRestrictedToSession ? null : (
                    <SelectItem value="__none">No organization</SelectItem>
                  )}
                  {orphanLabel ? (
                    <SelectItem value={orgStr}>Current value: {orgStr}</SelectItem>
                  ) : null}
                  {optionsList.map((t) => (
                    <SelectItem key={t.uid} value={t.name}>
                      {t.name}
                      {t.abbreviation ? ` (${t.abbreviation})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </ShadcnSelect>
              {organizationRestrictedToSession ? (
                <p className="text-xs text-gray-600 leading-relaxed">
                  Your account belongs to this organization. Collections you create or edit must use this tenant.
                </p>
              ) : null}
              {tenantsLoading ? (
                <p className="text-xs text-gray-500">Loading organizations…</p>
              ) : null}
              {tenantsError ? (
                <p className="text-xs text-red-600">{tenantsError}</p>
              ) : null}
              {typeof field.hint === "string" && !organizationRestrictedToSession && !(isNew && collectionWizardStepId === "review") ? (
                <p className="text-xs text-gray-500 leading-relaxed">{field.hint}</p>
              ) : null}
            </>,
            inline ? undefined : `${SETTING_FIELD_CARD} border-amber-100/80`
          );
        }
        case "menuIcon": {
          const iconValue = typeof getFieldValueByKey("menuIcon") === "string"
            ? String(getFieldValueByKey("menuIcon"))
            : "";
          return settingFieldShell(
            `${field.key}-${index}`,
            <>
              <div className="flex items-center space-x-2">
                {getFieldIcon(field.key)}
                <Label className={SETTING_FIELD_LABEL}>{field.label}</Label>
                <DebugFieldButton
                  fieldConfig={field}
                  fieldValue={iconValue}
                  label={field.label}
                />
              </div>
              <PhosphorFillIconPicker
                value={iconValue}
                onChange={(exportName) => {
                  setHasInteracted(true);
                  dispatch({
                    type: ActionTypes.EDIT_COMPONENT_VALUE_UPDATED,
                    payload: { key: "menuIcon", value: exportName },
                  });
                }}
              />
              {typeof field.hint === "string" ? (
                <p className="text-xs text-gray-500 leading-relaxed">{field.hint}</p>
              ) : null}
            </>
          );
        }
        case "checkbox":
          return (
            <div key={`${field.key}-${index}`} className={inline ? undefined : "mb-8"}>
              <FormControl fullWidth key={field.name}>
                <FormGroup aria-label={field.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!getFieldValueByKey(field.key)}
                        onChange={(event, checked) => {
                          console.log(event);
                          handleCheckboxChange(field, checked)
                        }
                        }
                        className="text-blue-600"
                        size="small"
                      />
                    }
                    label={<span className="text-sm font-medium text-gray-700">{field.label}</span>}
                    className={
                      inline
                        ? "m-0 w-full rounded-lg border border-blue-100 bg-blue-50/80 p-4 dark:border-blue-900/50 dark:bg-blue-950/20"
                        : "bg-blue-50 p-5 rounded-lg border border-blue-200"
                    }
                  />
                </FormGroup>
              </FormControl>
            </div>
          );
        case "arrayOfFields":
          return (
            <Card key={index} className="mb-6 border border-gray-300 bg-gray-50">
              <CardContent className="bg-white px-6 py-6">
                <SortableFields
                  fields={componentFields}
                  onFieldChange={handleChangeFormFieldToEdit}
                  onSortChange={handleFormFieldsSort}
                />
                <div className="mt-6 flex flex-wrap gap-2">
                  <ShadcnButton
                    onClick={addField}
                    className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-2 text-sm py-2.5 px-4"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Add Field</span>
                  </ShadcnButton>
                  <ShadcnButton
                    type="button"
                    variant="outline"
                    onClick={exportFieldsAsJson}
                    className="flex items-center space-x-2 text-sm py-2.5 px-4"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export fields</span>
                  </ShadcnButton>
                  <ShadcnButton
                    type="button"
                    variant="outline"
                    onClick={openImportDialog}
                    className="flex items-center space-x-2 text-sm py-2.5 px-4"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Import fields</span>
                  </ShadcnButton>
                  <input
                    ref={importFileInputRef}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={handleImportFileChange}
                  />
                </div>
              </CardContent>
            </Card>
          );
        default:
          return <></>;
      }
    }

  const fieldListDef = componentDef.find((f) => f.key === FIELD_LIST_DEF_KEY);

  const shouldShowSettingField = (
    field: any,
    opts: { onlyKeys?: Set<string>; excludeKeys?: Set<string>; compactSectionDescriptions?: boolean },
    layoutNew?: boolean
  ) => {
    const isCollectionField = field.key === "mongoCollection";
    const isAdvancedField = ["showGroups"].includes(field.key);
    const useNewRules = layoutNew ?? isNew;
    let shouldShow =
      ((!useNewRules || field.required) ||
        isCollectionField ||
        field.key === "organization" ||
        field.key === "menuFolder" ||
        field.key === "menuIcon" ||
        field.key === "fields") &&
      field.type !== "hidden";
    if (opts.onlyKeys && !opts.onlyKeys.has(field.key)) {
      shouldShow = false;
    }
    if (opts.excludeKeys && opts.excludeKeys.has(field.key)) {
      shouldShow = false;
    }
    if (field.key === "organization" && isOrganizationScopingDisabled()) {
      shouldShow = false;
    }
    if (isAdvancedField) {
      shouldShow = shouldShow && (isAdmin || canViewCollectionAdvanced);
    }
    if (isCollectionField) {
      shouldShow = shouldShow && isAdmin;
    }
    return shouldShow;
  };

  const renderCollectionSettingsSectionCards = (
    opts: { excludeKeys?: Set<string> }
  ) => {
    const sectionFields = new Map<string, { field: (typeof componentDef)[number]; index: number }[]>();

    componentDef.forEach((field, index) => {
      if (!shouldShowSettingField(field, opts)) return;
      const sectionId = field.section || "other";
      const list = sectionFields.get(sectionId) ?? [];
      list.push({ field, index });
      sectionFields.set(sectionId, list);
    });

    return COLLECTION_SETTING_SECTION_ORDER.map((sectionId) => {
      const items = sectionFields.get(sectionId);
      if (!items?.length) return null;
      const meta = collectionSettingsSectionMeta[sectionId];
      if (!meta) return null;

      return (
        <Card key={sectionId} className={SETTING_SECTION_CARD}>
          <CardHeader className="space-y-1 px-6 pb-3 pt-5">
            <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {meta.title}
            </CardTitle>
            {meta.description ? (
              <CardDescription className="text-xs leading-relaxed">{meta.description}</CardDescription>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-5 px-6 pb-6 pt-0">
            {items.map(({ field, index }) =>
              generateField(field, formValues[field.key], index, { inline: true })
            )}
          </CardContent>
        </Card>
      );
    });
  };

  const mapComponentSettingFields = (
    opts: { onlyKeys?: Set<string>; excludeKeys?: Set<string>; compactSectionDescriptions?: boolean },
    layoutNew?: boolean
  ) =>
    componentDef.map((field: any, index: number) => {
      const shouldShow = shouldShowSettingField(field, opts, layoutNew);

      const sectionId = settingsSectionHeaderIndex.get(index);
      const sectionMeta = sectionId ? collectionSettingsSectionMeta[sectionId] : null;

      return (
        <Fragment key={field.key || index}>
          {shouldShow && sectionMeta ? (
            <div
              className={
                firstSettingsSectionIndex === index
                  ? SETTING_SECTION_FIRST
                  : SETTING_SECTION_DIVIDER
              }
            >
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{sectionMeta.title}</h3>
              {sectionMeta.description ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug max-w-2xl">
                  {opts.compactSectionDescriptions && sectionId && WIZARD_REVIEW_SECTION_DESCRIPTION[sectionId]
                    ? WIZARD_REVIEW_SECTION_DESCRIPTION[sectionId]
                    : sectionMeta.description}
                </p>
              ) : null}
            </div>
          ) : null}
          {shouldShow ? <>{generateField(field, formValues[field.key], index)}</> : null}
        </Fragment>
      );
    });

  const fieldEditorDialog = (
    <Dialog
      open={showEditField && Boolean(editField?.uid)}
      onOpenChange={(open) => {
        if (!open) setShowEditField(false);
      }}
    >
      <DialogContent className="flex max-h-[min(90vh,880px)] w-full max-w-2xl flex-col gap-0 overflow-hidden border-gray-200 bg-white p-0 dark:border-gray-700 dark:bg-gray-900 sm:rounded-lg">
        <DialogHeader className="flex-shrink-0 space-y-0 border-b border-gray-200 px-6 py-4 text-left dark:border-gray-700">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <svg className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Field
          </DialogTitle>
        </DialogHeader>
        {editField?.uid ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <EditComponentField
              key={`${componentData.current.uid}-${editField.uid}`}
              onNewField={handleOnNewField}
              initialFieldData={editField}
              onFieldUpdated={handleOnFieldUpdated}
              autoFocusLabel={focusLabelOnNewField}
              onLabelFocused={() => setFocusLabelOnNewField(false)}
            />
            <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
              <ShadcnButton
                variant="destructive"
                onClick={() => requestDeleteField(editField)}
                className="flex w-full items-center space-x-2 py-2.5 text-sm"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Remove Field</span>
              </ShadcnButton>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <div
        className={`component-editor ${hideActionButtons ? "flex-shrink-0" : "min-h-screen bg-gray-50 p-8 pb-20"}`}
        onClick={handleEditorInteraction}
        onKeyDown={handleEditorInteraction}
      >
        <div className="max-w-7xl mx-auto">
          {isNew || !hideActionButtons ? (
            <div className={hideActionButtons ? "mb-4" : "mb-8"}>
              {!hideActionButtons ? (
                <>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 tracking-tight">
                    {isNew ? "Create collection" : `Edit collection`}
                  </h1>
                  {!isNew && component?.name ? (
                    <p className="text-base font-medium text-gray-800 dark:text-gray-200 mb-1">{component.name}</p>
                  ) : null}
                </>
              ) : null}
              {isNew ? (
                <>
                  <ol className="flex flex-wrap items-center gap-2 sm:gap-3 mt-4" aria-label="Creation steps">
                    {collectionWizardSteps.map(({ id, label }, i) => {
                      const n = i + 1;
                      const canGoBackToStep = n < newWizardStep;
                      const canGoForwardOneStep =
                        n === newWizardStep + 1 && newWizardStep < wizardTotalSteps && canAdvanceWizardFromStep(newWizardStep);
                      const stepIsNavButton = canGoBackToStep || canGoForwardOneStep;
                      const stepCircleClass = `flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${newWizardStep >= n
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        }`;
                      const stepLabelClass =
                        newWizardStep === n
                          ? "font-semibold text-gray-900 dark:text-gray-100"
                          : "text-gray-600 dark:text-gray-400";
                      return (
                        <li key={id} className="flex items-center gap-2 sm:gap-3 text-sm">
                          {stepIsNavButton ? (
                            <button
                              type="button"
                              onClick={() => goToWizardStep(n)}
                              className="flex items-center gap-2 sm:gap-3 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950"
                              aria-label={
                                canGoBackToStep
                                  ? `Go back to step ${n}: ${label}`
                                  : `Continue to step ${n}: ${label}`
                              }
                            >
                              <span className={stepCircleClass}>{n}</span>
                              <span className={`${stepLabelClass} underline-offset-2 hover:underline`}>{label}</span>
                            </button>
                          ) : (
                            <>
                              <span className={stepCircleClass}>{n}</span>
                              <span className={stepLabelClass}>{label}</span>
                            </>
                          )}
                          {i < collectionWizardSteps.length - 1 ? (
                            <span className="text-gray-300 dark:text-gray-600 hidden sm:inline" aria-hidden>
                              /
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ol>
                  <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed mt-3">
                    {collectionWizardStepId === "identity"
                      ? "Choose a display name. The URL slug is created from the name and can be used to quickly access your collection in the future."
                      : collectionWizardStepId === "fields"
                        ? "Add columns and form fields for each item."
                        : collectionWizardStepId === "organization"
                          ? "Optionally assign this collection to an organization so managers in that tenant inherit item access."
                          : collectionWizardStepId === "storage"
                            ? "Choose the MongoDB collection name used to store items. Use Suggest for a unique name from the slug."
                            : collectionWizardStepId === "review"
                              ? "Review the summary, then create the collection."
                              : ""}
                  </p>
                </>
              ) : !hideActionButtons ? (
                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed">
                  Switch between settings and field definitions below. Save when you are done.
                </p>
              ) : null}
            </div>
          ) : null}

          {updateError && (
            <div className="mb-6">
              <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 text-red-900 dark:text-red-200 p-5 shadow-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <AlertTitle className="text-lg font-semibold mb-2">Collection Update Failed</AlertTitle>
                    <AlertDescription className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                      {updateError}
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            </div>
          )}

          {isNew ? (
            <>
              {collectionWizardStepId === "identity" ? (
                <Card className="shadow-md border border-gray-200/80 bg-white dark:bg-gray-900/50 overflow-hidden max-w-3xl">
                  <CardContent className="space-y-3 bg-transparent px-8 py-8">
                    {mapComponentSettingFields({ onlyKeys: IDENTITY_SETTING_KEYS }, true)}
                    {wizardStep1IdentityWarning ? (
                      <Alert
                        className="mt-6 sm:mt-8 border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
                      >
                        <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        <AlertTitle className="text-sm font-semibold">Duplicate URL slug</AlertTitle>
                        <AlertDescription className="text-sm">{wizardStep1IdentityWarning}</AlertDescription>
                      </Alert>
                    ) : null}
                  </CardContent>
                </Card>
              ) : null}

              {collectionWizardStepId === "fields" ? (
                <div className="max-w-5xl">
                  {fieldListDef ? generateField(fieldListDef, formValues[fieldListDef.key], componentDef.indexOf(fieldListDef)) : null}
                </div>
              ) : null}

              {collectionWizardStepId === "organization" ? (
                <Card className="shadow-md border border-gray-200/80 bg-white dark:bg-gray-900/50 overflow-hidden max-w-3xl">
                  <CardContent className="space-y-3 bg-transparent px-8 py-8">
                    {mapComponentSettingFields({ onlyKeys: new Set(["organization"]) }, true)}
                  </CardContent>
                </Card>
              ) : null}

              {collectionWizardStepId === "storage" ? (
                <Card className="shadow-md border border-gray-200/80 bg-white dark:bg-gray-900/50 overflow-hidden max-w-3xl">
                  <CardContent className="space-y-3 bg-transparent px-8 py-8">
                    {mapComponentSettingFields({ onlyKeys: new Set(["mongoCollection"]) }, true)}
                  </CardContent>
                </Card>
              ) : null}

              {collectionWizardStepId === "review" ? (
                <div className="max-w-3xl mx-auto space-y-5">
                  <Card className="shadow-md border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
                    <CardHeader className="pb-2 pt-6 px-6 space-y-1">
                      <CardTitle className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Summary
                      </CardTitle>
                      <CardDescription className="text-sm text-gray-500 dark:text-gray-400">
                        Identity and field layout from the previous steps.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="px-6 pb-6 pt-0 space-y-5">
                      <dl className="grid grid-cols-1 sm:grid-cols-[7rem_1fr] gap-x-4 gap-y-3 text-sm">
                        <dt className="text-gray-500 dark:text-gray-400 pt-0.5">Name</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100 break-words min-w-0">
                          {(component?.name || "").trim() || "—"}
                        </dd>
                        <dt className="text-gray-500 dark:text-gray-400 pt-0.5">Slug</dt>
                        <dd className="min-w-0">
                          <Badge variant="secondary" className="font-mono text-sm font-normal">
                            {(component?.slug || "").trim() || "—"}
                          </Badge>
                        </dd>
                        <dt className="text-gray-500 dark:text-gray-400 pt-0.5">Organization</dt>
                        <dd className="font-medium text-gray-900 dark:text-gray-100 break-words min-w-0">
                          {organizationRestrictedToSession
                            ? sessionOrganizationLabel || "—"
                            : (organizationForSave || "").trim() || "—"}
                        </dd>
                        {isAdmin ? (
                          <>
                            <dt className="text-gray-500 dark:text-gray-400 pt-0.5">MongoDB collection</dt>
                            <dd className="min-w-0">
                              <Badge variant="secondary" className="font-mono text-sm font-normal">
                                {(component?.mongoCollection || "").trim() || "—"}
                              </Badge>
                            </dd>
                          </>
                        ) : null}
                      </dl>
                      <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Fields</span>
                          <Badge variant="outline" className="tabular-nums text-xs font-normal">
                            {componentFields.length}
                          </Badge>
                        </div>
                        {componentFields.length > 0 ? (
                          <ul className="flex flex-wrap gap-2">
                            {componentFields.map((f: any) => (
                              <li key={f.uid}>
                                <Badge variant="outline" className="font-normal max-w-full truncate">
                                  {(f.label || f.name || "Untitled") + (f.type ? ` · ${f.type}` : "")}
                                </Badge>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-600 dark:text-gray-400 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-950/40 px-3 py-2.5">
                            None yet. After you create the collection you can add fields from the editor.
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : null}
            </>
          ) : (
            <Tabs value={editMainTab} onValueChange={(v) => setEditMainTab(v as "settings" | "fields")} className="w-full">
              <TabsList className={COLLECTION_EDITOR_TABS_LIST}>
                <TabsTrigger value="settings" className={COLLECTION_EDITOR_TABS_TRIGGER}>
                  Collection settings
                </TabsTrigger>
                <TabsTrigger value="fields" className={COLLECTION_EDITOR_TABS_TRIGGER}>
                  Fields ({componentFields.length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="settings" className={COLLECTION_EDITOR_TABS_CONTENT}>
                <div className={`${COLLECTION_EDITOR_TABS_PANEL} ${SETTING_SECTION_GRID} p-5 sm:p-6`}>
                  {renderCollectionSettingsSectionCards({ excludeKeys: new Set([FIELD_LIST_DEF_KEY]) })}
                </div>
              </TabsContent>
              <TabsContent value="fields" className={COLLECTION_EDITOR_TABS_CONTENT}>
                <div className={`${COLLECTION_EDITOR_TABS_PANEL} max-w-5xl p-6`}>
                  {fieldListDef ? generateField(fieldListDef, formValues[fieldListDef.key], componentDef.indexOf(fieldListDef)) : null}
                </div>
              </TabsContent>
            </Tabs>
          )}

          {!hideActionButtons && (
            <div className="mt-8 mb-6 flex flex-wrap justify-end gap-3">
              {isNew && newWizardStep > 1 ? (
                <ShadcnButton
                  type="button"
                  variant="outline"
                  onClick={() => setNewWizardStep((s) => (s > 1 ? s - 1 : s))}
                  className="px-6 py-2.5 text-sm"
                >
                  Back
                </ShadcnButton>
              ) : null}
              <ShadcnButton variant="outline" onClick={handleCancelClick} className="px-6 py-2.5 text-sm">
                Cancel
              </ShadcnButton>
              <ShadcnButton
                onClick={() => void tryAdvanceNewWizardOrSave()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 text-sm shadow-md hover:shadow-lg transition-all duration-200"
              >
                {isNew ? (collectionWizardStepId === "review" ? "Create collection" : "Next") : "Save changes"}
              </ShadcnButton>
            </div>
          )}
        </div>
      </div>

      {fieldEditorDialog}

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent className="sm:max-w-[425px] mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <span>Discard Changes?</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-600">
              Are you sure you want to close this form? Any unsaved changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
            <ShadcnButton
              onClick={() => {
                setShowCancelConfirm(false);
              }}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Continue Editing
            </ShadcnButton>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Field Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteFieldConfirm} onOpenChange={setShowDeleteFieldConfirm}>
        <AlertDialogContent className="sm:max-w-[425px] mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <span>Remove Field?</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-600">
              This will permanently remove{" "}
              <span className="font-medium text-gray-900">
                {pendingDeleteField?.label || pendingDeleteField?.name || "this field"}
              </span>
              .
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
            <ShadcnButton
              onClick={() => {
                setShowDeleteFieldConfirm(false);
                setPendingDeleteField(null);
              }}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Cancel
            </ShadcnButton>
            <AlertDialogAction
              onClick={handleConfirmDeleteField}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
            >
              Remove Field
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import fields dialog: choose all vs select certain fields */}
      <Dialog open={showImportFieldsDialog} onOpenChange={(open) => { if (!open) setImportError(null); setShowImportFieldsDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import fields</DialogTitle>
            <DialogDescription>
              Import all fields to the collection configuration, or select certain fields only?
            </DialogDescription>
          </DialogHeader>
          {importError && (
            <Alert variant="destructive" className="py-2">
              <AlertDescription>{importError}</AlertDescription>
            </Alert>
          )}
          {importCandidateFields.length > 0 && (
            <>
              <div className="flex gap-4 py-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="importMode"
                    checked={importMode === 'all'}
                    onChange={() => setImportMode('all')}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Import all fields</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="importMode"
                    checked={importMode === 'select'}
                    onChange={() => setImportMode('select')}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Select certain fields only</span>
                </label>
              </div>
              {importMode === 'select' && (
                <div className="max-h-48 overflow-y-auto border rounded p-3 space-y-2">
                  <div className="flex gap-2 text-sm text-gray-600 mb-2">
                    <button type="button" onClick={selectAllImportFields} className="underline">Select all</button>
                    <span>|</span>
                    <button type="button" onClick={selectNoneImportFields} className="underline">Select none</button>
                  </div>
                  {importCandidateFields.map((f: any, i: number) => {
                    const label = typeof f?.label === 'string' ? f.label : (f?.name ?? `Field ${i + 1}`);
                    const name = typeof f?.name === 'string' ? f.name : '';
                    return (
                      <label key={i} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={selectedImportIndices.has(i)}
                          onChange={() => toggleImportFieldSelection(i)}
                          className="rounded border-gray-300"
                        />
                        <span>{label}{name && label !== name ? ` (${name})` : ''}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <ShadcnButton type="button" variant="outline" onClick={() => { setShowImportFieldsDialog(false); setImportError(null); }}>
              Cancel
            </ShadcnButton>
            <ShadcnButton type="button" onClick={applyImportedFields} disabled={importCandidateFields.length === 0}>
              Import
            </ShadcnButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

EditComponent.displayName = "EditComponent";

export default EditComponent;